import { PlusIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { isLogicalFilter } from '@/sections/insights/queryBuilder/utils/filterTypeGuards';
import type { QueryFilter } from '@/types';

import { LeafFilterCard } from './LeafFilterCard';
import { LogicalFilterGroup } from './LogicalFilterGroup';

interface FiltersSectionProps {
  filters: QueryFilter[];
  editable: boolean;
  onChange?: (filters: QueryFilter[]) => void;
  suggestedFields?: string[];
}

export const FiltersSection = ({
  filters,
  editable,
  onChange,
  suggestedFields,
}: FiltersSectionProps) => {
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const pendingConvertAction = useRef<(() => void) | null>(null);

  const handleAddFilter = () => {
    onChange?.([...(filters || []), { field: '', operator: '=', value: '' }]);
  };

  const handleDeleteFilter = (index: number) => {
    onChange?.(filters.filter((_, idx) => idx !== index));
  };

  const handleUpdateFilter = (index: number, updates: Partial<QueryFilter>) => {
    const updated = [...filters];
    const currentFilter = updated[index];
    const newOperator = updates.operator || currentFilter.operator;
    const wasLogical = isLogicalFilter(currentFilter);
    const willBeLogical = newOperator === 'AND' || newOperator === 'OR';

    // Case 1: Regular → AND/OR (initialize with one empty child)
    if (!wasLogical && willBeLogical) {
      updated[index] = {
        ...currentFilter,
        ...updates,
        field: '',
        operator: newOperator,
        value: [{ field: '', operator: '=', value: '' }],
      };
      onChange?.(updated);
      return;
    }

    // Case 2: AND/OR → Regular (confirm data loss)
    if (wasLogical && !willBeLogical && updates.operator) {
      const children = currentFilter.value as QueryFilter[];
      if (Array.isArray(children) && children.length > 0) {
        pendingConvertAction.current = () => {
          const refreshed = [...filters];
          refreshed[index] = {
            ...refreshed[index],
            ...updates,
            value: '',
          };
          onChange?.(refreshed);
        };
        setShowConvertDialog(true);
        return;
      }

      updated[index] = {
        ...currentFilter,
        ...updates,
        value: '',
      };
      onChange?.(updated);
      return;
    }

    // Case 3: Regular update or AND ↔ OR
    updated[index] = { ...updated[index], ...updates };
    onChange?.(updated);
  };

  // Recursive filter rendering
  const renderFilter = (
    filter: QueryFilter,
    index: number,
    depth: number = 0
  ): ReactNode => {
    if (isLogicalFilter(filter)) {
      return (
        <LogicalFilterGroup
          key={index}
          filter={filter as QueryFilter & { value: QueryFilter[] }}
          index={index}
          depth={depth}
          editable={editable}
          onUpdate={updates => handleUpdateFilter(index, updates)}
          onDelete={() => handleDeleteFilter(index)}
          renderFilter={renderFilter}
        />
      );
    }

    return (
      <LeafFilterCard
        key={index}
        filter={filter}
        index={index}
        depth={depth}
        editable={editable}
        onUpdate={updates => handleUpdateFilter(index, updates)}
        onDelete={() => handleDeleteFilter(index)}
        suggestedFields={suggestedFields}
      />
    );
  };

  return (
    <>
      <div>
        <h4 className="text-chariot-text-primary mb-1 text-[10px] font-semibold">
          Filters
        </h4>
        {!filters || filters.length === 0 ? (
          editable ? (
            <div className="space-y-1 py-2 text-left">
              <button
                onClick={handleAddFilter}
                className="inline-flex items-center gap-0.5 rounded px-2 py-0.5 text-[9px] font-medium text-blue-600 hover:bg-blue-50"
              >
                <PlusIcon className="h-2.5 w-2.5" />
                Add
              </button>
            </div>
          ) : (
            <div className="py-2 text-center">
              <p className="text-chariot-text-secondary text-[9px]">
                No filters
              </p>
            </div>
          )
        ) : (
          <div className="space-y-2">
            {filters.map((filter, idx) => renderFilter(filter, idx, 0))}

            {editable && (
              <button
                onClick={handleAddFilter}
                className="mt-1 inline-flex items-center gap-0.5 rounded px-2 py-0.5 text-[9px] font-medium text-blue-600 hover:bg-blue-50"
              >
                <PlusIcon className="h-2.5 w-2.5" />
                Add Filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Convert group to regular filter confirmation dialog */}
      <AlertDialog
        open={showConvertDialog}
        onOpenChange={open => {
          if (!open) {
            pendingConvertAction.current = null;
          }
          setShowConvertDialog(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Nested Filters</AlertDialogTitle>
            <AlertDialogDescription>
              Changing to a regular operator will remove nested filters.
              Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                pendingConvertAction.current = null;
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                pendingConvertAction.current?.();
                pendingConvertAction.current = null;
                setShowConvertDialog(false);
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
